import { request } from "./client";
import { API_URL, authHeaders } from "./config";
import type { CanonicalRecord } from "@/types";

export interface IntakePreview {
  record: CanonicalRecord;
  categoryOptions: string[];
  methodOptions: string[];
  /** Display names are separate from saved method lookup values. Optional for older servers. */
  methodOptionLabels?: Record<string, string>;
  factorOptions: Array<{ id: number; name: string }>;
}

interface PreviewRequest {
  chatId: string;
  batchId: string;
  recordId: number;
  edits: Record<string, string | number>;
  token: string;
  signal?: AbortSignal;
}
interface Pending extends PreviewRequest {
  resolve: (result: IntakePreview) => void;
  reject: (error: Error) => void;
  cleanup: () => void;
  cancelBatch?: () => void;
}
const pending: Pending[] = [];
let scheduled: ReturnType<typeof setTimeout> | undefined;
let running = 0;
const aborted = () => new DOMException("Preview cancelled", "AbortError");

function pump() {
  while (running < 2 && pending.length) {
    const first = pending.shift()!;
    const batch = [first];
    for (let index = 0; index < pending.length && batch.length < 25;) {
      const item = pending[index];
      if (
        item.chatId === first.chatId &&
        item.batchId === first.batchId &&
        item.token === first.token &&
        !batch.some((entry) => entry.recordId === item.recordId)
      ) {
        batch.push(...pending.splice(index, 1));
      } else index++;
    }
    const live = batch.filter((item) => !item.signal?.aborted);
    if (!live.length) continue;
    const controller = new AbortController();
    for (const item of live)
      item.cancelBatch = () => {
        if (live.every((entry) => entry.signal?.aborted)) controller.abort();
      };
    running++;
    void request<{
      results: Array<{
        recordId: number;
        preview?: IntakePreview;
        error?: string;
      }>;
    }>("post", `${API_URL}/api/emissions/intake/preview-batch`, {
      data: {
        chatId: first.chatId,
        batchId: first.batchId,
        records: live.map(({ recordId, edits }) => ({ recordId, edits })),
      },
      ...authHeaders(first.token),
      signal: controller.signal,
      timeout: 130_000,
    })
      .then(({ results }) => {
        for (const item of live) {
          if (item.signal?.aborted) continue;
          const result = results.find(
            (entry) => entry.recordId === item.recordId,
          );
          if (result?.preview) item.resolve(result.preview);
          else
            item.reject(
              new Error(
                result?.error || "Could not preview this record. Try again.",
              ),
            );
        }
      })
      .catch((error: Error) => {
        for (const item of live) if (!item.signal?.aborted) item.reject(error);
      })
      .finally(() => {
        for (const item of live) item.cleanup();
        running--;
        pump();
      });
  }
}

/** Coalesce row previews so navigating a review does not spend one API request per row. */
export function queueIntakePreview(
  input: PreviewRequest,
): Promise<IntakePreview> {
  return new Promise((resolve, reject) => {
    if (input.signal?.aborted) {
      reject(aborted());
      return;
    }
    const entry: Pending = { ...input, resolve, reject, cleanup: () => {} };
    const cancel = () => {
      const index = pending.indexOf(entry);
      if (index !== -1) pending.splice(index, 1);
      entry.cancelBatch?.();
      entry.cleanup();
      reject(aborted());
    };
    entry.cleanup = () => input.signal?.removeEventListener("abort", cancel);
    input.signal?.addEventListener("abort", cancel, { once: true });
    pending.push(entry);
    if (!scheduled)
      scheduled = setTimeout(() => {
        scheduled = undefined;
        pump();
      }, 25);
  });
}
